import { Injectable } from '@angular/core';
import { ReplaySubject, Subject, Subscription } from 'rxjs';

import TaskLists = gapi.client.tasks.TaskLists;
import TaskList = gapi.client.tasks.TaskList;
import Tasks = gapi.client.tasks.Tasks;
import Task = gapi.client.tasks.Task;

import { TasksApiService } from './api/tasks-api.service';
import { TasksClearParams, TasksDeleteParams, TasksGetParams, TasksInsertParams, TasksListParams, TasksMoveParams, TasksPatchParams } from './api/tasks-resource-method-params';
import { TasklistsDeleteParams, TasklistsInsertParams, TasklistsPatchParams } from './api/tasklists-resource-method-params';

const DEBUG = true;

@Injectable({
  providedIn: 'root'
})
export class StoreService {
  filter: TasksListParams = { tasklist: ''};
  taskLists$ = new ReplaySubject<TaskLists>(1);
  taskListsSubscription: Subscription;
  taskList?: TaskList;

  tasks$ = new ReplaySubject<Tasks|null>(1);
  tasksSubscription?: Subscription;
  /**
   * The last item selected in the `TasksComponent` view.
   */
  task$ = new Subject<Task|null>();
  /**
   * Selecting an item in the `SubtasksComponent` view changes the current `focus$`
   * but not the current `task$`.
   */
  focus$ = new Subject<Task|null>();

  constructor(private tasksApi: TasksApiService) {
    this.task$.subscribe(task => this.focus$.next(task));
    if (DEBUG) {this.focus$.subscribe(task => console.debug('New Task selection:', task));}
    this.taskListsSubscription = this.tasksApi.listTaskLists$().subscribe(taskLists => {
      this.taskLists$.next(taskLists);
    });
}

  updateFilter(params: TasksListParams): void {
    if (DEBUG) {console.log('Store updateFilter() to:', params);}
    this.filter = params;
    if (this.taskList) {
      if (this.tasksSubscription) {this.tasksSubscription.unsubscribe();}
      this.tasksSubscription = this.tasksApi.listTasks$(this.filter).subscribe(tasks => {
        this.tasks$.next(tasks);
      });
    }
  }

  onTaskListSelected(taskList: TaskList): void {
    this.taskList = taskList;
    this.filter.tasklist = taskList.id!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    this.task$.next(null);
    if (this.tasksSubscription) {this.tasksSubscription.unsubscribe();}
    this.tasksSubscription = this.tasksApi.listTasks$(this.filter).subscribe(tasks => {
      this.tasks$.next(tasks);
    });
  }

  onTaskListAdded(request: TasklistsInsertParams): void {
    void this.tasksApi.insertTaskList(request).then(result => {
      this.tasks$.next(null);
      if (this.taskListsSubscription) {this.taskListsSubscription.unsubscribe();}
      this.taskListsSubscription = this.tasksApi.listTaskLists$().subscribe(taskLists => {
        this.taskLists$.next(taskLists);
      });
      this.taskLists$.subscribe(tasklists => {
        const added = tasklists.items?.find(tasklist => tasklist.id===result.id) || null;
        if (!added) {console.error('Failed to find returned tasklist %o in tasklists %o', result, tasklists);}
        else {this.onTaskListSelected(added);} // eslint-disable-line @typescript-eslint/no-non-null-assertion
      }).unsubscribe();
    }).catch(err => console.error(err));
  }

  onTaskListDeleted(request: TasklistsDeleteParams): void {
    void this.tasksApi.deleteTaskList(request).then(() => {
      this.taskList = undefined;
      this.task$.next(null);
      this.tasks$.next(null);
      if (this.taskListsSubscription) {this.taskListsSubscription.unsubscribe();}
      this.taskListsSubscription = this.tasksApi.listTaskLists$().subscribe(taskLists => {
        this.taskLists$.next(taskLists);
      });
    }).catch(err => console.error(err));
  }

  onTaskListUpdated(request: TasklistsPatchParams): void {
    void this.tasksApi.patchTaskList(request).then(result => {
      if (this.taskListsSubscription) {this.taskListsSubscription.unsubscribe();}
      this.taskListsSubscription = this.tasksApi.listTaskLists$().subscribe(taskLists => {
        this.taskLists$.next(taskLists);
      });
      this.taskLists$.subscribe(tasklists => {
        const updated = tasklists.items?.find(tasklist => tasklist.id===result.id) || null;
        if (!updated) {console.error('Failed to find returned tasklist %o in tasklists %o', result, tasklists);}
        this.onTaskListSelected(updated!); // eslint-disable-line @typescript-eslint/no-non-null-assertion
      }).unsubscribe();
    }).catch(err => console.error(err));
  }

  onTaskListCleared(request: TasksClearParams): void {
    if (DEBUG) {console.log('Clearing completed tasks.');}
    void this.tasksApi.clearTasks(request).then(() => {
      if (DEBUG) {console.log('Cleared completed tasks.');}
      if (this.tasksSubscription) {this.tasksSubscription.unsubscribe();}
      this.tasksSubscription = this.tasksApi.listTasks$(this.filter).subscribe(tasks => {
        this.tasks$.next(tasks);
      });
    }).catch(err => console.error(err));
  }

  onTaskSelected(task: Task): void {
    this.task$.next(task);
  }

  onTaskAdded(request: TasksInsertParams): void {
    void this.tasksApi.insertTask(request).then(result => {
      if (this.tasksSubscription) {this.tasksSubscription.unsubscribe();}
      this.tasksSubscription = this.tasksApi.listTasks$(this.filter).subscribe(tasks => {
        this.tasks$.next(tasks);
        const added = tasks?.items?.find(task => task.id===result.id) || null;
        if (!added) {console.error('Failed to find returned task %o in tasks %o', result, tasks);}
        this.task$.next(added);
      });
    }).catch(err => console.error(err));
  }

  onTaskDeleted(request: TasksDeleteParams): void {
    void this.tasksApi.deleteTask(request).then(() => {
      if (this.tasksSubscription) {this.tasksSubscription.unsubscribe();}
      this.tasksSubscription = this.tasksApi.listTasks$(this.filter).subscribe(tasks => {
        this.tasks$.next(tasks);
        this.task$.next(null);
      });
    }).catch(err => console.error(err));
  }

  onTaskUpdated(request: TasksPatchParams): void {
    void this.tasksApi.patchTask(request).then(result => {
      if (DEBUG) {console.log('Task updated:', result);}
      if (this.tasksSubscription) {this.tasksSubscription.unsubscribe();}
      this.tasksSubscription = this.tasksApi.listTasks$(this.filter).subscribe(tasks => {
        this.tasks$.next(tasks);
        const updated = tasks.items?.find(task => task.id===result.id) || null;
        if (!updated) {console.error('Failed to find returned task %o in tasks %o', result, tasks);}
        if (DEBUG) {console.log('Matched updated task:', updated);}
        this.task$.next(updated);
      });
    }).catch(err => console.error(err));
  }

  onTaskMoved(request: TasksMoveParams): void {
    void this.tasksApi.moveTask(request).then(result => {
      this.tasksSubscription = this.tasksApi.listTasks$(this.filter).subscribe(tasks => {
        this.tasks$.next(tasks);
        const moved = tasks.items?.find(task => task.id===result.id) || null;
        if (!moved) {console.error('Failed to find returned task %o in tasks %o', result, tasks);}
        if (request.parent) {
          this.focus$.next(moved);
        }
        else {
          this.task$.next(moved);
        }
      });
    }).catch(err => console.error(err));
  }

  onStepSelected(step: Task): void {
    this.focus$.next(step);
  }

  onStepAdded(request: TasksInsertParams): void {
    void this.tasksApi.insertTask(request).then(result => {
      if (this.tasksSubscription) {this.tasksSubscription.unsubscribe();}
      this.tasksSubscription = this.tasksApi.listTasks$(this.filter).subscribe(tasks => {
        this.tasks$.next(tasks);
        const added = tasks.items?.find(task => task.id===result.id) || null;
        if (!added) {console.error('Failed to find returned task %o in tasks %o', result, tasks);}
        this.focus$.next(added);
      });
    }).catch(err => console.error(err));
  }

  onStepDeleted(request: TasksDeleteParams): void {
    void this.tasksApi.deleteTask(request).then(() => {
      if (this.tasksSubscription) {this.tasksSubscription.unsubscribe();}
      this.tasksSubscription = this.tasksApi.listTasks$(this.filter).subscribe(tasks => {
        this.tasks$.next(tasks);
        this.focus$.next(null);
      });
    }).catch(err => console.error(err));
  }

  onStepUpdated(request: TasksPatchParams): void {
    void this.tasksApi.patchTask(request).then(result => {
      if (this.tasksSubscription) {this.tasksSubscription.unsubscribe();}
      this.tasksSubscription = this.tasksApi.listTasks$(this.filter).subscribe(tasks => {
        this.tasks$.next(tasks);
        const updated = tasks.items?.find(task => task.id===result.id) || null;
        if (!updated) {console.error('Failed to find returned task %o in tasks %o', result, tasks);}
        this.focus$.next(updated);
      });
    }).catch(err => console.error(err));
  }

  getTask(request: TasksGetParams): Promise<Task> {
    return this.tasksApi.getTask(request);
  }
}
